import type { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { tokens } from "../theme";

/**
 * A control that is not a TextField, wearing a TextField's label.
 *
 * MUI floats an outlined label ON the border, so it costs no vertical space and
 * the input box starts at the top of the field. A label rendered as a line of
 * text above a control costs ~24px, so that control drops half a row below every
 * TextField beside it and the row stops reading as a row - which is exactly what
 * happened to Fulfilment next to Price, and to the timetable before it.
 *
 * Same geometry, then: absolutely positioned, sitting on the control's own
 * border with a slice of surface behind it.
 */
export default function LabelledControl({
  label,
  helperText,
  children,
}: {
  label: string;
  helperText?: string;
  children: ReactNode;
}) {
  return (
    <Box>
      <Box sx={{ position: "relative" }}>
        <Typography
          variant="meta"
          component="span"
          sx={{
            position: "absolute",
            top: -8,
            left: 9,
            px: 0.5,
            zIndex: 1,
            backgroundColor: tokens.surface,
            lineHeight: 1.35,
            color: tokens.inkMuted,
            pointerEvents: "none",
          }}
        >
          {label}
        </Typography>
        {children}
      </Box>
      {helperText ? (
        <Typography variant="micro" sx={{ color: tokens.inkFaint, mt: 0.5 }}>
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}
